import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  Container
} from '@mui/material';
import {
  ArrowBack,
  SystemUpdate,
  Android,
  Apple,
  Save,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const AppVersionPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Sürüm durumları
  const [minAndroidVersionCode, setMinAndroidVersionCode] = useState<number | string>('');
  const [latestAndroidVersionCode, setLatestAndroidVersionCode] = useState<number | string>('');
  const [minIosVersion, setMinIosVersion] = useState('');
  const [latestIosVersion, setLatestIosVersion] = useState('');
  const [updateUrlAndroid, setUpdateUrlAndroid] = useState('');
  const [updateUrlIos, setUpdateUrlIos] = useState('');
  const [forceUpdateMessage, setForceUpdateMessage] = useState('');
  const [optionalUpdateMessage, setOptionalUpdateMessage] = useState('');

  const getHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchVersionSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/system/version`, {
        headers: getHeaders()
      });
      if (response.data.success) {
        const data = response.data.data;
        setMinAndroidVersionCode(data.minAndroidVersionCode ?? '');
        setLatestAndroidVersionCode(data.latestAndroidVersionCode ?? '');
        setMinIosVersion(data.minIosVersion ?? '');
        setLatestIosVersion(data.latestIosVersion ?? '');
        setUpdateUrlAndroid(data.updateUrlAndroid ?? '');
        setUpdateUrlIos(data.updateUrlIos ?? '');
        setForceUpdateMessage(data.forceUpdateMessage ?? '');
        setOptionalUpdateMessage(data.optionalUpdateMessage ?? '');
      }
    } catch (err: any) {
      console.error('Fetch version settings error:', err);
      setError(err.response?.data?.message || 'Versiyon ayarları alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');

    // Basit doğrulama
    if (
      !minAndroidVersionCode ||
      !latestAndroidVersionCode ||
      !minIosVersion ||
      !latestIosVersion ||
      !updateUrlAndroid ||
      !updateUrlIos ||
      !forceUpdateMessage ||
      !optionalUpdateMessage
    ) {
      setError('Lütfen tüm alanları doldurun.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        minAndroidVersionCode: Number(minAndroidVersionCode),
        latestAndroidVersionCode: Number(latestAndroidVersionCode),
        minIosVersion,
        latestIosVersion,
        updateUrlAndroid,
        updateUrlIos,
        forceUpdateMessage,
        optionalUpdateMessage
      };

      const response = await axios.post(`${API_BASE_URL}/api/system/version`, payload, {
        headers: getHeaders()
      });

      if (response.data.success) {
        setSuccessMsg('Uygulama sürüm ayarları başarıyla güncellendi. Kullanıcılara anında yansıtılacaktır.');
        setTimeout(() => setSuccessMsg(''), 5000);
      }
    } catch (err: any) {
      console.error('Save version settings error:', err);
      setError(err.response?.data?.message || 'Ayarlar kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchVersionSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <SystemUpdate sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Uygulama Versiyon Yönetimi (Force Update)
          </Typography>
          <IconButton color="inherit" onClick={fetchVersionSettings} disabled={loading}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
            <CircularProgress color="primary" size={50} />
          </Box>
        ) : (
          <form onSubmit={handleSaveSettings}>
            <Grid container spacing={3}>
              {/* Android Sürüm Ayarları */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', mr: 2 }}>
                        <Android sx={{ color: '#4CAF50' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        Android Versiyon Ayarları
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      type="number"
                      label="Minimum Zorunlu Versiyon Kodu (Min Version Code)"
                      variant="outlined"
                      helperText="Bu kodun altındaki tüm sürümler uygulamayı güncellemeye zorlanır. (örn: 27)"
                      value={minAndroidVersionCode}
                      onChange={(e) => setMinAndroidVersionCode(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      type="number"
                      label="En Güncel Versiyon Kodu (Latest Version Code)"
                      variant="outlined"
                      helperText="Min ile Latest arasındaki sürümlere güncelleme önerilir, fakat kapatılabilir. (örn: 28)"
                      value={latestAndroidVersionCode}
                      onChange={(e) => setLatestAndroidVersionCode(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      label="Google Play Store Linki"
                      variant="outlined"
                      value={updateUrlAndroid}
                      onChange={(e) => setUpdateUrlAndroid(e.target.value)}
                      sx={{ mb: 3 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* iOS Sürüm Ayarları */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', mr: 2 }}>
                        <Apple sx={{ color: '#000000' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>
                        iOS Versiyon Ayarları
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      label="Minimum Zorunlu Sürüm (Min Version Name)"
                      variant="outlined"
                      placeholder="1.0.0"
                      helperText="Bu sürümün altındaki tüm cihazlar güncellenmeye zorlanır. (örn: 1.0.0)"
                      value={minIosVersion}
                      onChange={(e) => setMinIosVersion(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      label="En Güncel Sürüm (Latest Version Name)"
                      variant="outlined"
                      placeholder="1.1.0"
                      helperText="Min ile Latest arasındaki cihazlara güncelleme önerilir, fakat kapatılabilir. (örn: 1.1.0)"
                      value={latestIosVersion}
                      onChange={(e) => setLatestIosVersion(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      label="Apple App Store Linki"
                      variant="outlined"
                      value={updateUrlIos}
                      onChange={(e) => setUpdateUrlIos(e.target.value)}
                      sx={{ mb: 3 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Uyarı Mesajları Ayarları */}
              <Grid size={{ xs: 12 }}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', mb: 3 }}>
                      Kullanıcı Mesajları Yapılandırması
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Zorunlu Güncelleme Mesajı"
                          variant="outlined"
                          helperText="Çok eski sürüme sahip kullanıcılara zorunlu kilit ekranında gösterilecek mesaj."
                          value={forceUpdateMessage}
                          onChange={(e) => setForceUpdateMessage(e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="İsteğe Bağlı Güncelleme Mesajı"
                          variant="outlined"
                          helperText="Yakın sürümdeki kullanıcılara gösterilecek, 'Daha Sonra' seçeneği sunan mesaj."
                          value={optionalUpdateMessage}
                          onChange={(e) => setOptionalUpdateMessage(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Kaydet butonu */}
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  sx={{
                    bgcolor: '#8B4513',
                    '&:hover': { bgcolor: '#70370f' },
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(139, 69, 19, 0.2)'
                  }}
                >
                  {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Container>
    </Box>
  );
};

export default AppVersionPage;
